import { css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { BaseElement } from "../ui-kit/BaseElement";
import { designTokensCss } from "../ui-kit/designTokens";
import { t } from "../../translation/i18n";
import i18next from "i18next";
import { AuthSession } from "./AuthSession";
import { plannerLogout, plannerMe } from "./AuthApi";
import type { AuthRecord } from "./AuthTypes";

@customElement("sugar-auth-account")
export class SugarAuthAccount extends BaseElement {
  static override styles = css`
    ${designTokensCss}

    :host {
      display: none;
      font-family: var(--srd-font);
    }

    :host([signed-in]) {
      display: inline-flex;
      position: relative;
    }

    .chip {
      appearance: none;
      max-width: 220px;
      min-height: 36px;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 0 12px;
      border: 1px solid var(--srd-border-strong);
      border-radius: var(--srd-radius-pill);
      background: var(--srd-surface-solid);
      color: var(--srd-text);
      font: inherit;
      font-size: 0.75rem;
      font-weight: 600;
      cursor: pointer;
      box-shadow: var(--srd-shadow-sm);
    }

    .chip:hover {
      background: var(--srd-view-toggle-hover-bg);
    }

    .email {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .menu {
      position: absolute;
      top: calc(100% + 6px);
      right: 0;
      min-width: 180px;
      padding: 6px;
      border: 1px solid var(--srd-border);
      border-radius: var(--srd-radius-md);
      background: var(--srd-surface-solid);
      box-shadow: var(--srd-shadow-md);
      z-index: 20;
    }

    .menu button {
      appearance: none;
      width: 100%;
      min-height: 36px;
      border: 0;
      border-radius: var(--srd-radius-sm);
      background: transparent;
      color: var(--srd-text);
      font: inherit;
      font-size: 0.8125rem;
      font-weight: 600;
      text-align: left;
      padding: 0 10px;
      cursor: pointer;
    }

    .menu button:hover {
      background: var(--srd-bg-muted);
    }
  `;

  @state()
  private _record: AuthRecord | null = null;

  @state()
  private _open = false;

  protected useShadowRoot = true;

  private _unsubscribe?: () => void;
  private _onDocClick?: (event: MouseEvent) => void;

  override connectedCallback(): void {
    super.connectedCallback();
    this._sync();
    this._unsubscribe = AuthSession.subscribe(() => this._sync());
    void this._hydrateUser();
    this._onDocClick = (event: MouseEvent) => {
      if (!this._open) return;
      const path = event.composedPath();
      if (!path.includes(this)) this._open = false;
    };
    document.addEventListener("click", this._onDocClick);
  }

  override disconnectedCallback(): void {
    this._unsubscribe?.();
    if (this._onDocClick) document.removeEventListener("click", this._onDocClick);
    super.disconnectedCallback();
  }

  private _sync() {
    this._record = AuthSession.get();
    this.toggleAttribute("signed-in", !!this._record?.ticket);
    if (!this._record) this._open = false;
  }

  private async _hydrateUser() {
    const record = AuthSession.get();
    if (record?.ticket && !record.user?.email) {
      await plannerMe();
    }
  }

  private _label(): string {
    const user = this._record?.user;
    return user?.email || user?.username || i18next.t("authAccount");
  }

  private async _logout() {
    this._open = false;
    await plannerLogout();
  }

  protected renderHtml() {
    if (!this._record?.ticket) return html``;
    return html`
      <button
        type="button"
        class="chip"
        aria-haspopup="menu"
        aria-expanded=${this._open ? "true" : "false"}
        title=${this._label()}
        @click=${(e: Event) => {
          e.stopPropagation();
          this._open = !this._open;
        }}
      >
        <span class="email">${this._label()}</span>
      </button>
      ${
        this._open
          ? html`
              <div class="menu" role="menu">
                <button type="button" role="menuitem" @click=${() => this._logout()}>
                  ${t("authLogout")}
                </button>
              </div>
            `
          : nothing
      }
    `;
  }
}
